import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Trenger restauranten din en bedre nettside?",
  },
  {
    id: "definition",
    label: "Hva bør en restaurantnettside gjøre i dag?",
  },
  {
    id: "visibility",
    label: "Hvorfor online synlighet er viktig for restauranter",
  },
  {
    id: "menu",
    label: "Menyen er ofte den viktigste delen av en restaurantnettside",
  },
  {
    id: "trust",
    label: "En nettside bygger tillit før gjesten kommer",
  },
  {
    id: "reservation",
    label: "Nettside, meny og reservasjon bør fungere sammen",
  },
  {
    id: "mistakes",
    label: "Vanlige feil på restaurantnettsider",
  },
  {
    id: "small",
    label: "Trenger små restauranter også en nettside?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper med å bygge en restaurantnettside",
  },
  {
    id: "faq",
    label: "FAQ om restaurantnettsider",
  },
];

const checklist = [
  "Restauranten din har ingen nettside eller bare en veldig gammel nettside.",
  "Menyen din er vanskelig å finne online.",
  "Gjester spør ofte om åpningstider, priser eller menyen.",
  "Du vil koble QR-kode, meny og reservasjon på ett sted.",
  "Du vil være mindre avhengig av sosiale medier.",
  "Restauranten din bør være enklere å forstå i Google og online søk.",
  "Du vil vise bilder, tilbud eller aktuell informasjon enklere.",
  "Du vil hjelpe gjester med å bestemme seg før de besøker restauranten.",
  "Du vil ha en enkel side uten et komplisert byråprosjekt.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "En enkel online tilstedeværelse kan være nok foreløpig",
      text:
        "Hvis gjester allerede finner restauranten din lett, og den viktigste informasjonen er tydelig online, haster det kanskje ikke med en ny nettside ennå.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "En restaurantnettside ville være nyttig",
      text:
        "Restauranten din kan ha nytte av en tydelig nettside: meny, åpningstider, kontakt, reservasjon og QR-kode kan samles på ett sted.",
      tone: "medium",
    };
  }

  return {
    title: "En nettside kan bli ditt digitale sentrum",
    text:
      "Flere punkter viser at restauranten din bør bruke en nettside som et sentralt digitalt sted: synlighet, meny, reservasjon, tillit og bedre orientering for gjester.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantWebsiteForRestaurantsNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Restaurantnettside: meny, reservasjon og tillit på ett sted | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        quickTarget="check"
        quickLabel="Rask sjekk"
        langLinks={{
          de: "/de/restaurant-website",
          en: "/en/restaurant-website-for-restaurants",
          no: "/no/restaurant-nettside",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurantnettside for restauranter: meny, reservasjon og tillit
              på ett sted
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En restaurantnettside er ikke bare et digitalt visittkort. Den
                kan være stedet der gjester ser menyen, sjekker åpningstider,
                ser bilder og reserverer bord.
              </p>

              <p className="seoHero__text">
                Sosiale medier, Google og anmeldelsesplattformer er viktige. Men
                din egen nettside er stedet restauranten selv kontrollerer:
                innhold, meny, bilder, tilbud, reservasjon og kontakt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Trenger restauranten din en bedre nettside?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: ikke alle restauranter trenger en stor nettside med
              en gang, men nesten alle restauranter har nytte av ett tydelig sted
              for meny, åpningstider, kontakt og reservasjon.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Hva bør en restaurantnettside gjøre i dag?</h2>

          <p>
            En god restaurantnettside svarer raskt på gjestens viktigste
            spørsmål: Hva kan jeg spise der? Hvor ligger restauranten? Når er
            den åpen? Hvordan kan jeg reservere bord?
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digital meny.</li>
            <li className="text__seo-li">Åpningstider.</li>
            <li className="text__seo-li">Adresse og kontakt.</li>
            <li className="text__seo-li">Bilder av mat og atmosfære.</li>
            <li className="text__seo-li">Reservasjon.</li>
            <li className="text__seo-li">QR-kode-tilgang.</li>
            <li className="text__seo-li">Aktuelle tilbud.</li>
            <li className="text__seo-li">Mobilvisning.</li>
          </ul>

          <p>
            Den beste restaurantnettsiden er ofte ikke den mest kompliserte. Det
            er siden som hjelper gjesten med å ta en beslutning raskt.
          </p>
        </section>

        <section id="visibility" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor online synlighet er viktig for restauranter</h2>

            <p>
              Mange gjester oppdager restauranter online før de noen gang står
              foran døren: via Google, kart, sosiale medier, anbefalinger eller
              direkte søk etter meny, åpningstider eller reservasjon.
            </p>

            <h3>Nettsiden din kan bli det sentrale målet</h3>

            <p>
              En nettside er ikke den eneste kanalen, men den kan være det
              sentrale stedet som andre kanaler peker til: Google-profil,
              QR-kode, sosiale medier, flyere eller digital meny.
            </p>

            <p>
              Dette hjelper gjester med å finne én tydelig kilde i stedet for å
              hoppe mellom ulike plattformer, utdaterte PDF-filer, sosiale
              innlegg og separate kontaktlenker.
            </p>
          </div>
        </section>

        <section id="menu" className="acquaintance">
          <h2>Menyen er ofte den viktigste delen av en restaurantnettside</h2>

          <p>
            Mange besøkende kommer til en restaurantnettside med et veldig
            konkret spørsmål: Hva kan jeg spise der, og hva koster det?
          </p>

          <p>
            Derfor bør menyen ikke være skjult, utdatert eller bare tilgjengelig
            som en PDF som er vanskelig å lese. En mobil digital meny med
            kategorier, bilder og aktuelle priser kan være mye tydeligere.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/digital-meny-for-restauranter">
                  Digital meny for restauranter
                </Link>
              </p>
            </div>

            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/qr-kode-meny-for-restauranter">
                  QR-kode-meny for restauranter
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="trust" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>En nettside bygger tillit før gjesten kommer</h2>

            <p>
              Gjester bestemmer seg ikke bare ut fra pris og avstand. De legger
              også merke til inntrykket, hvor aktuell informasjonen virker,
              bildene, tydeligheten og om restauranten virker profesjonell.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Aktuell meny.</li>
              <li className="text__seo-li">Ekte bilder.</li>
              <li className="text__seo-li">Tydelige åpningstider.</li>
              <li className="text__seo-li">Kontaktinformasjon.</li>
              <li className="text__seo-li">Reservasjonsmulighet.</li>
              <li className="text__seo-li">Sterk mobilopplevelse.</li>
            </ul>

            <p>
              Hvis denne informasjonen mangler eller er utdatert, kan gjesten
              bli usikker. En god nettside reduserer denne usikkerheten.
            </p>

            <VisualIdea>
              En forhåndsvisning av en restaurantnettside på smarttelefon: meny,
              bilder, åpningstider og en tydelig reservasjonsknapp på ett sted.
            </VisualIdea>
          </div>
        </section>

        <section id="reservation" className="acquaintance">
          <h2>Nettside, meny og reservasjon bør fungere sammen</h2>

          <p>
            En restaurantnettside blir sterkere når den ikke bare informerer, men
            også gjør neste steg enkelt. Gjesten ser menyen, blir interessert og
            kan reservere bord.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Finn restaurant.</li>
            <li className="text__seo-li">Se meny.</li>
            <li className="text__seo-li">Sjekk atmosfære.</li>
            <li className="text__seo-li">Reserver bord.</li>
          </ol>

          <p>
            Denne veien er enklere enn å hoppe mellom ulike plattformer, PDF-er,
            meldinger og telefonnumre.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/online-bordreservasjon-for-restauranter">
                  Online bordreservasjon
                </Link>
              </p>
            </div>

            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/gratis-digital-meny-for-restauranter">
                  Gratis digital meny
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige feil på restaurantnettsider</h2>

            <p>
              En restaurantnettside trenger ikke være stor, men den må svare
              tydelig på gjestens viktigste spørsmål.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Gamle priser eller utilgjengelige retter blir liggende
                    online.
                  </li>
                  <li className="text__seo-li">
                    Menyen finnes bare som PDF.
                  </li>
                  <li className="text__seo-li">
                    Gjester finner ikke enkelt hvordan de reserverer.
                  </li>
                  <li className="text__seo-li">
                    Mobilvisningen er svak.
                  </li>
                  <li className="text__seo-li">
                    Adresse, åpningstider eller kontaktinformasjon er vanskelig
                    å finne.
                  </li>
                  <li className="text__seo-li">
                    Nettside, QR-kode og digital meny er ikke koblet sammen.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Hold menyen oppdatert.</li>
                  <li className="text__seo-li">Bruk en mobil digital meny.</li>
                  <li className="text__seo-li">
                    Legg til en tydelig reservasjonsvei.
                  </li>
                  <li className="text__seo-li">
                    Gjør nettsiden behagelig på smarttelefon.
                  </li>
                  <li className="text__seo-li">
                    Vis åpningstider, adresse og kontakt tydelig.
                  </li>
                  <li className="text__seo-li">
                    Koble nettside, QR-kode og meny i én flyt.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="small" className="acquaintance">
          <h2>Trenger små restauranter også en nettside?</h2>

          <p>
            Små restauranter tenker ofte at sosiale medier eller Google er nok.
            Disse kanalene er viktige, men de tilhører ikke restauranten fullt
            ut. En egen nettside gir mer kontroll.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Bare eksterne plattformer</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Mindre kontroll.</li>
                <li className="text__seo-li">
                  Avhengig av andre plattformers layout og regler.
                </li>
                <li className="text__seo-li">
                  Informasjon spres ofte på flere kanaler.
                </li>
                <li className="text__seo-li">
                  Meny og reservasjon er ikke alltid koblet sammen.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Egen restaurantnettside</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Sentralt sted for gjester.</li>
                <li className="text__seo-li">Egen digital meny.</li>
                <li className="text__seo-li">Tydelig reservasjon.</li>
                <li className="text__seo-li">QR-kode-destinasjon.</li>
                <li className="text__seo-li">
                  Bedre kontrollert presentasjon.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om restaurantnettsider</h2>

          <div className="seoFaq">
            <details>
              <summary>Trenger alle restauranter sin egen nettside?</summary>

              <p>
                Ikke alle restauranter trenger en stor nettside med en gang. Men
                nesten alle restauranter har nytte av ett tydelig sted for meny,
                åpningstider, kontakt og reservasjon.
              </p>
            </details>

            <details>
              <summary>Er sosiale medier nok?</summary>

              <p>
                Sosiale medier er nyttige, men ikke fullt kontrollerbare. En
                nettside er mer stabil som et sentralt sted for viktig
                informasjon.
              </p>
            </details>

            <details>
              <summary>Hva er det viktigste innholdet?</summary>

              <p>
                For mange gjester er meny, åpningstider, adresse, kontakt,
                bilder og reservasjon de viktigste delene.
              </p>
            </details>

            <details>
              <summary>Bør menyen legges inn som PDF?</summary>

              <p>
                En PDF er mulig, men ofte upraktisk på smarttelefon. En mobil
                digital meny er vanligvis mer brukervennlig.
              </p>
            </details>

            <details>
              <summary>Kan en nettside kobles med en QR-kode?</summary>

              <p>
                Ja. En QR-kode kan lede gjester direkte til menyen eller
                restaurantsiden.
              </p>
            </details>

            <details>
              <summary>Kan jeg starte enkelt?</summary>

              <p>
                Ja. En god start kan inneholde meny, QR-kode, kontakt og en
                enkel restaurantside.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="business"
          exclude="/no/restaurant-nettside"
        />
      </article>
    </main>
  );
}